import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { View, TouchableOpacity, StyleSheet, ScrollView, Dimensions } from 'react-native';
import { useSelector } from 'react-redux';
import { i18nNS, useComponentTKey } from '../../i18n';
import { selectExpensePendingList } from '../../store/selectors/expense.selector';
import { Color, CommonProperties, CommonStyles } from '../../styles';
import { scale } from '../../utils/scale.utils';
import BaseText from '../BaseText';
import Button from '../Button';
import { CheckboxInner } from '../Checkbox';
import Icon from '../Icon';
import Modal from '../Modal';
import RowView from '../RowView';
import ExpenseReportCardInfo from './ExpenseReportCardInfo';
import { Screens } from '../../constants/ScreenNames';
import { useNavigation } from '@react-navigation/native';

const { height: screenHeight } = Dimensions.get('window'); // Get screen height

const MoveExpenseModal = ({ visible, originExpenseId, onClose, onSubmit }) => {
  const { t } = useTranslation(i18nNS.tripExpense);
  const { tKey } = useComponentTKey('ExpenseScreen');
  const [selectedExpense, setSelectedExpense] = useState(null);
  const expensePendingList = useSelector(selectExpensePendingList);
  const navigation = useNavigation();

  useEffect(() => {
    if (originExpenseId === selectedExpense) {
      setSelectedExpense(null);
    }
  }, [originExpenseId]);

  const filteredExpenseList = useMemo(() => {
    return expensePendingList.filter((item) => item.ReportId !== originExpenseId);
  }, [expensePendingList, originExpenseId]);

  const showExpenseList = useMemo(() => {
    return (
      <ScrollView contentContainerStyle={stylesModal.scroll}>
        {filteredExpenseList.map((item) => (
          <TouchableOpacity
            key={item.ReportId}
            style={CommonStyles.flex1}
            onPress={() => setSelectedExpense(item.ReportId)}
          >
            <RowView style={stylesModal.item}>
              <CheckboxInner selected={item.ReportId === selectedExpense} />
              <ExpenseReportCardInfo style={CommonStyles.ml3} item={item} />
            </RowView>
          </TouchableOpacity>
        ))}
      </ScrollView>
    );
  }, [filteredExpenseList, selectedExpense]);

  if (!visible) {
    return null;
  }

  return (
    <Modal visible={visible} transparent animationType="none">
      <View style={stylesModal.overlay}>
        <View style={stylesModal.modalContent}>
          {/* Header */}
          <View style={stylesModal.header}>
            <BaseText style={stylesModal.title}>{t(tKey('MoveReceipt'))}</BaseText>
            <TouchableOpacity onPress={onClose} style={stylesModal.close}>
              <Icon name="cancel" color={Color.primary} size={Math.round(scale(20))} />
            </TouchableOpacity>
          </View>

          {/* Scrollable List */}
          <View style={stylesModal.listContainer}>{showExpenseList}</View>

          {/* Fixed Footer */}
          <View style={stylesModal.footer}>
            <TouchableOpacity style={CommonStyles.my5} onPress={() => setSelectedExpense(null)}>
              <RowView>
                <CheckboxInner selected={selectedExpense === null} />
                <BaseText style={stylesModal.new}>{t(tKey('NewReport'))}</BaseText>
              </RowView>
            </TouchableOpacity>
            <Button
              label={t('common:Continue')}
              style={[CommonStyles.mt3, stylesModal.submitButton]}
              labelStyle={[CommonStyles.buttonFont]}
              onPress={() => {
                onSubmit(selectedExpense);
                navigation.navigate(Screens.Expense);
              }}
            />
          </View>
        </View>
      </View>
    </Modal>
  );
};

const stylesModal = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)', // Semi-transparent background
    justifyContent: 'center', // Center content vertically
    alignItems: 'center', // Center content horizontally
  },
  modalContent: {
    width: '90%', // Adjust width as needed
    height: screenHeight * 0.8, // 80% of the screen height
    backgroundColor: Color.accent,
    borderRadius: scale(10),
    padding: scale(10),
    justifyContent: 'space-between', // Space between list and footer
    elevation: 5, // Shadow for Android
    shadowColor: Color.black, // Shadow for iOS
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
  },
  header: {
    position: 'relative', // Ensure the title is positioned relative to the header
    justifyContent: 'center', // Center the title horizontally
    alignItems: 'center', // Center the title vertically
    marginBottom: scale(10),
  },
  title: {
    textAlign: 'center',
    ...CommonProperties.h2,
  },
  close: {
    position: 'absolute', // Float the close button
    top: 0, // Align to the top of the header
    right: 0, // Align to the right of the header
    padding: scale(3),
  },
  listContainer: {
    flex: 1, // Take up available space for the list
    marginBottom: scale(10),
  },
  item: {
    flex: 1,
    alignItems: 'center',
    ...CommonProperties.mb3,
  },
  new: {
    ...CommonProperties.h3,
    ...CommonProperties.ml3,
  },
  footer: {
    borderTopWidth: 1,
    borderTopColor: Color.lightgray,
    paddingTop: scale(10),
  },
  scroll: {
    flexGrow: 1,
  },
  submitButton: {
    ...CommonProperties.py2,
    ...CommonProperties.shadow,
    ...CommonProperties.borderRadius4,
  },
});

export default React.memo(MoveExpenseModal);
